"use client";
"use no memo";

// TanStack Table v8 mutates stable table instances; visibility state must be read live.

import { MixerHorizontalIcon } from "@radix-ui/react-icons";
import { Column, VisibilityState } from "@tanstack/react-table";
import * as React from "react";

import { Button } from "@/src/components/shadcn/ui/button";
import { Checkbox } from "@/src/components/shadcn/ui/checkbox";
import {
  Command,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
  CommandEmpty,
} from "@/src/components/shadcn/ui/command";
import { Popover, PopoverTrigger, PopoverContent } from "@/src/components/shadcn/ui/popover";
import { camelCaseToHuman } from "@/src/lib/string";

interface DataTableViewOptionsProps {
  table: any;
}

const COLUMN_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
  appointmentEhrId: "EHR ID",
  documentClassification: "Classification",
  ehrReason: "EHR Reason",
  ehrStatus: "EHR Status",
  ehrType: "EHR Type",
  npiNumber: "NPI Number",
  patientEhrId: "Patient EHR ID",
};

export function DataTableViewOptions({ table }: DataTableViewOptionsProps) {
  const [search, setSearch] = React.useState("");
  const columns = table.getAllColumns().filter((column: Column<any, unknown>) => column.getCanHide());

  const getColumnDisplayName = (column: Column<any, unknown>) =>
    COLUMN_DISPLAY_NAME_OVERRIDES[column.id] ?? camelCaseToHuman(column.id);

  const filteredColumns = columns.filter((column: Column<any, unknown>) =>
    getColumnDisplayName(column).toLowerCase().includes(search.toLowerCase())
  );

  const updateColumnVisibility = React.useCallback(
    (column: Column<any, unknown>) => {
      table.setColumnVisibility((prev: VisibilityState) => ({
        ...prev,
        [column.id]: !column.getIsVisible(),
      }));
    },
    [table]
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-accent border-dashed">
          <MixerHorizontalIcon className="mr-2 h-4 w-4" />
          View
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] bg-popover p-0" align="end">
        <Command>
          <CommandInput placeholder="Columns" value={search} onValueChange={setSearch} className="" />
          <CommandList>
            <CommandEmpty>No columns found.</CommandEmpty>
            <CommandGroup>
              {filteredColumns.map((column: any) => (
                <CommandItem
                  key={column.id}
                  onSelect={() => {
                    updateColumnVisibility(column);
                  }}
                  className=""
                >
                  <Checkbox
                    checked={column.getIsVisible()}
                    onCheckedChange={() => {
                      updateColumnVisibility(column);
                    }}
                    className="mr-2 border-purple-300"
                  />
                  <span className="flex-1 truncate">{getColumnDisplayName(column)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
